import { useState, type FormEvent } from 'react';

export interface PartnerAccountSetupStepProps {
  initialEmail: string;
  initialPassword: string;
  /** Bridges the collected credentials up to PartnerSignUpPage, which threads them down into the later,
   * reused AdminInfoStep as `initialOwnerEmail`/`initialOwnerPassword` — the actual `createOwner` call still
   * happens there, at the same point in the pipeline Admin Sign Up already uses (see AdminInfoStep's own
   * doc comment on these props). Nothing is sent to the backend from this step itself. */
  onContinue: (email: string, password: string) => void;
}

/**
 * Step 1 — Account Setup (Partner Sign Up only; Admin Sign Up has no equivalent screen). Client-side only:
 * no backend call happens here. The approved Partner design collects the sign-in email/password BEFORE
 * business information, the opposite order from Admin Sign Up (which collects it last, at Step 4/Admin
 * Information) — rather than build a second, parallel owner-creation code path, this screen just holds the
 * values in memory until the existing `createOwner` call later in the pipeline (reused AdminInfoStep) runs,
 * identical to how Admin Sign Up creates its Owner.
 */
export function PartnerAccountSetupStep({ initialEmail, initialPassword, onContinue }: PartnerAccountSetupStepProps) {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState(initialPassword);
  const [confirmPassword, setConfirmPassword] = useState(initialPassword);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    onContinue(email, password);
  }

  return (
    <div className="screen active" data-testid="partner-account-setup-step">
      <div className="screen-icon" aria-hidden="true">
        🔐
      </div>
      <h2>Step 1: Account Setup</h2>
      <p className="section-label">Create the email and password you&rsquo;ll use to sign in to your Partner account.</p>

      <form onSubmit={handleSubmit}>
        <div className="field-grid">
          <div className="field full">
            <label htmlFor="partnerAccountEmail">
              Email <span className="req">*</span>
            </label>
            <input
              className="control"
              id="partnerAccountEmail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              autoComplete="email"
              required
              autoFocus
            />
          </div>

          <div className="field">
            <label htmlFor="partnerAccountPassword">
              Password <span className="req">*</span>
            </label>
            <div className="password-wrap">
              <input
                className="control"
                id="partnerAccountPassword"
                type={passwordVisible ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
              <button
                className="eye-btn"
                type="button"
                aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                onClick={() => setPasswordVisible((v) => !v)}
              >
                {passwordVisible ? '🙈' : '👁'}
              </button>
            </div>
          </div>

          <div className="field">
            <label htmlFor="partnerAccountConfirmPassword">
              Confirm Password <span className="req">*</span>
            </label>
            <input
              className="control"
              id="partnerAccountConfirmPassword"
              type={passwordVisible ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>
        </div>

        {error && (
          <p className="error-line" role="alert">
            <span className="info-icon">i</span>
            <span>{error}</span>
          </p>
        )}

        <div className="actions">
          <button className="btn btn-primary" type="submit">
            Save and Continue
          </button>
        </div>
      </form>
    </div>
  );
}
